import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as creditNoteItemActions from '@redux/modules/creditNoteItem'

import defaultFormState, { requiredFields } from '@models/creditNoteItem'

import type { CreditNoteItemModel, CreditNoteItemRequestOptions } from '@models/types'
import type { DeleteParams } from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

type CreateCreditNoteItemParams = {
  creditNoteItemParams: Partial<CreditNoteItemModel>,
  dispatch: AppDispatch,
  requestOptions?: CreditNoteItemRequestOptions,
}

const createCreditNoteItem = (params: CreateCreditNoteItemParams) => {
  const { dispatch, creditNoteItemParams, requestOptions } = params
  const { createCreditNoteItem: createFn } = creditNoteItemActions

  return dispatch(createFn(creditNoteItemParams, requestOptions))
}

type DeleteCreditNoteItemParams = {
  creditNoteItem: DeleteParams<CreditNoteItemModel>,
  dispatch: AppDispatch,
}

const deleteCreditNoteItem = (params: DeleteCreditNoteItemParams) => {
  const { dispatch, creditNoteItem } = params
  const { deleteCreditNoteItem: deleteFn } = creditNoteItemActions

  return dispatch(deleteFn(creditNoteItem))
}

export const useRelations = (creditNoteItem: Partial<CreditNoteItemModel> = {}) => {
  const { credit_note_id } = creditNoteItem

  const { creditNotes } = useSelector(reduxState => reduxState.entities)

  const creditNote = credit_note_id && creditNotes[credit_note_id]
    ? creditNotes[credit_note_id] : {}

  return {
    creditNote,
  }
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCreditNoteItemForm(
  creditNoteItem: Partial<CreditNoteItemModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const creditNoteItemForm = useForm(
    defaultFormState,
    { entity: creditNoteItem, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [creditNoteItem.id, creditNoteItem.cache_key],
  )

  return {
    ...creditNoteItemForm,
  }
}

function useCreditNoteItem(initEntity: Partial<CreditNoteItemModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: creditNoteItem }: { entity: CreditNoteItemModel} = useLatestEntity(initEntity, 'creditNoteItems')

  const dispatch = useDispatch()

  const { creditNote } = useRelations(creditNoteItem)

  const {
    // @ts-expect-error TS2339
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.creditNoteItems)

  return {
    callbacks: {
      createCreditNoteItem: (
        creditNoteItemParams: Partial<CreditNoteItemModel>,
        entityOptions?: CreditNoteItemRequestOptions,
      ) => (
        createCreditNoteItem({ creditNoteItemParams, dispatch, requestOptions: entityOptions })
      ),
      deleteCreditNoteItem: () => deleteCreditNoteItem({ dispatch, creditNoteItem }),
    },
    creating,
    creditNote,
    creditNoteItem,
    deleting,
    loading: loading && !loadedIds.includes(creditNoteItem.id),
    updating,
  }
}

export type UseCreditNoteItemPayload = ReturnType<typeof useCreditNoteItem>

export default useCreditNoteItem
